import React, { useId, useState } from "react";
import { strings } from "../../resources/strings";
import {
  MAX_TEXT_LENGTH,
  resolveLocalizedEntry,
  langAttribute,
} from "../../utils/questionUtils";
import { LocalizedParticipantQuestion, QuestionType } from "../../apis/API";

/** Answer payload emitted by a text question (subset of IdentifiableAnswerV3Input). */
export interface TextAnswer {
  questionId: string;
  textValue: string;
  questionType: QuestionType.text;
}

export interface TextQuestionProps {
  question: LocalizedParticipantQuestion;
  /** Prior answer to re-display on resume / back navigation (flow engine). */
  defaultValue?: string;
  onAnswer: (answer: TextAnswer) => void;
}

/**
 * Free-text question component. Renders the API-provided question text and a
 * capped textarea. Presentational; the flow engine (IHA-347) owns sequencing
 * and assembles the full V3 answer payload. Also the text-input foundation
 * reused by workflow text fields (IHA-346).
 */
const TextQuestion: React.FC<TextQuestionProps> = ({
  question,
  defaultValue,
  onAnswer,
}) => {
  const [value, setValue] = useState<string>(defaultValue ?? "");
  const headlineId = useId();

  const questionId = question.id ?? "";
  const headlineEntry = resolveLocalizedEntry(question.localization);
  const headline = headlineEntry?.text ?? "";
  // SC 3.1.2 — the resolver falls back across languages, so text can arrive in
  // a language the document does not declare. undefined when it matches.
  const headlineLang = langAttribute(headlineEntry?.locale);
  // Submit is gated here (and via the button's `disabled` below) on both a
  // non-empty value and a present question id — so we never emit an answer with
  // an empty questionId. This single gate is the only guard; a duplicate
  // early-return in handleSubmit would be unreachable behind `disabled`.
  const canSubmit = value.trim().length > 0 && Boolean(questionId);

  const handleSubmit = () => {
    onAnswer({
      questionId,
      textValue: value.trim(),
      questionType: QuestionType.text,
    });
  };

  return (
    // Scrollable column so the textarea + submit stay reachable above the
    // virtual keyboard on iPad / mobile browsers.
    <div
      data-testid="text-question"
      className="flex flex-col gap-6 overflow-y-auto"
    >
      <h2 id={headlineId} lang={headlineLang} className="text-2xl text-black">
        {headline}
      </h2>

      <textarea
        data-testid="text-answer"
        value={value}
        maxLength={MAX_TEXT_LENGTH}
        onChange={(e) => setValue(e.target.value)}
        placeholder={strings.questionAnswers.textPlaceholder}
        aria-labelledby={headlineId}
        aria-label={strings.questionAnswers.textPlaceholder}
        className="w-full min-h-[8rem] p-2 border border-primary rounded-lg"
      />

      <button
        type="button"
        onClick={handleSubmit}
        disabled={!canSubmit}
        className="self-end min-h-[44px] px-6 rounded-2xl bg-primary text-accent font-semibold disabled:opacity-30"
      >
        {strings.submitAnswers}
      </button>
    </div>
  );
};

export default TextQuestion;
